// lendcord-core's IndianDateFormatModule serializes/deserializes every
// java.time.LocalDate field (dob, nominee.dateOfBirth, installmentStartDate)
// as dd-MM-yyyy on the wire, not ISO — deliberately strict, ISO input is
// rejected with a 400. Native <input type="date"> elements only ever speak
// ISO yyyy-MM-dd for their value/onChange though (a browser constraint, not
// something CSS/locale can change), so every LocalDate-backed form needs to
// convert at the request/response boundary: ISO while bound to the picker,
// dd-MM-yyyy on the wire. These three functions are that boundary — nothing
// else in the app should touch a wire-format LocalDate string directly.

const WIRE_DATE_REGEX = /^(\d{2})-(\d{2})-(\d{4})$/;
const ISO_DATE_REGEX = /^(\d{4})-(\d{2})-(\d{2})$/;

// "20-05-1995" -> "1995-05-20" — a LocalDate field's wire value to the ISO
// shape a native <input type="date">'s value prop requires. Empty string
// (not null) on a malformed/empty input, matching how these fields are
// always optional form state, never rendered as an error.
export function wireDateToIso(wireDate: string): string {
  const match = WIRE_DATE_REGEX.exec(wireDate);
  if (!match) return "";
  const [, day, month, year] = match;
  return `${year}-${month}-${day}`;
}

// "1995-05-20" -> "20-05-1995" — a native date input's ISO value to the
// dd-MM-yyyy shape lendcord-core's LocalDate fields require on the wire.
export function isoToWireDate(isoDate: string): string {
  const match = ISO_DATE_REGEX.exec(isoDate);
  if (!match) return "";
  const [, year, month, day] = match;
  return `${day}-${month}-${year}`;
}

// Human-readable display for a wire-format (dd-MM-yyyy) LocalDate value —
// same digits, slash-separated per this app's DD/MM/YYYY display
// convention. Falls back to the raw value if it isn't wire-shaped, so a
// display site never throws on unexpected input.
export function formatWireDate(wireDate: string): string {
  const match = WIRE_DATE_REGEX.exec(wireDate);
  if (!match) return wireDate;
  const [, day, month, year] = match;
  return `${day}/${month}/${year}`;
}

// ISO date-only strings ("1995-05-20") from the mock-backed lender pages
// (dashboard/reports/creditor — see root CLAUDE.md's "What is NOT here yet")
// — Mockoon's fixture data, untouched by
// lendcord-core's dd-MM-yyyy wire format, so this is deliberately a
// separate function from formatWireDate above, not a shared one. Rearranges
// the regex groups directly rather than round-tripping through a Date
// object, sidestepping the UTC-midnight-rollback trap a date-only ISO
// string hits if parsed with `new Date(iso)`.
export function formatIsoDate(isoDate: string): string {
  const match = ISO_DATE_REGEX.exec(isoDate);
  if (!match) return isoDate;
  const [, year, month, day] = match;
  return `${day}/${month}/${year}`;
}

// Timestamps (createdAt, requestedAt, uploadedAt, ...) are full ISO-8601
// datetimes — a separate wire format from LocalDate, untouched by
// IndianDateFormatModule (that module only registers for java.time.
// LocalDate, not Instant/OffsetDateTime) — parse with the native Date
// constructor (which does understand ISO datetimes) and render in the same
// DD/MM/YYYY convention as formatWireDate above, for one consistent look
// app-wide.
export function formatTimestampDate(isoDateTime: string): string {
  const parsed = new Date(isoDateTime);
  const day = String(parsed.getDate()).padStart(2, "0");
  const month = String(parsed.getMonth() + 1).padStart(2, "0");
  const year = parsed.getFullYear();
  return `${day}/${month}/${year}`;
}
